"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { LoadingSpinner } from "@/components/assets_old/LoadingSpinner";
import { SupportFormModal } from "@/components/SupportFormModal";
import { useAuthStore } from "@/services/Cognito/authStore";
import { useMutation } from "@tanstack/react-query";
import { twMerge } from "tailwind-merge";

type SubscriptionTier = "Annual" | "Monthly";

type PricingCardProps = {
  title: string;
  price: string;
  period: string;
  description: Array<string>;
  handleClickButton: () => void;
  className?: string;
  buttonClassName?: string;
  loadingState: SubscriptionTier | null;
  spinnerClassName: string;
  discount?: ReactNode;
  highlighted?: boolean;
};

const PricingCard = (props: PricingCardProps) => {
  const baseContainerClass =
    "flex w-full max-w-[300px] flex-col rounded-2xl border-2 px-6 py-8 shadow-xl transition-transform hover:scale-105";
  const baseButtonClass =
    "text-base font-bold py-3 rounded-lg w-full grid justify-center items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white";
  const loading = props.loadingState === props.title;

  return (
    <div className="flex select-none justify-center">
      <div
        className={twMerge(
          baseContainerClass,
          props.highlighted
            ? "border-amber-400 bg-violet-700 text-white ring-4 ring-amber-400"
            : "border-violet-200 bg-white",
          props.className
        )}
      >
        <div className="relative">
          <h2 className="text-xl font-semibold">{props.title}</h2>
          {props.discount}
        </div>
        <div className="py-4">
          <span className="text-5xl font-extrabold">{props.price}</span>
          <span className="text-lg opacity-80">/{props.period}</span>
        </div>
        <div className="flex h-full flex-col justify-between">
          <ul className="flex flex-col gap-3 pb-6 text-left">
            {props.description.map((bullet, index) => (
              <li className="flex items-start gap-2" key={bullet}>
                <span className="mt-1 text-green-400">&#10003;</span>
                <span className={index === 0 ? "font-bold" : ""}>{bullet}</span>
              </li>
            ))}
          </ul>
          <button
            className={twMerge(props.buttonClassName, baseButtonClass)}
            onClick={props.handleClickButton}
          >
            {loading ? (
              <LoadingSpinner className={props.spinnerClassName} />
            ) : (
              <span>Subscribe Now</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ValueProp = ({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: string;
}) => (
  <div className="flex flex-col items-center text-center">
    <span className="mb-3 text-4xl">{icon}</span>
    <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

const PricingPage = () => {
  const router = useRouter();
  const showAuthenticator = useAuthStore((ctx) => ctx.showAuthenticator);
  const user = useAuthStore((ctx) => ctx.user);
  const [loading, setLoading] = useState<SubscriptionTier | null>(null);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = useState(false);

  const { mutate, isLoading } = useMutation({
    mutationFn: async (tier: SubscriptionTier) => {
      const routeURL = new URL(
        "api/stripe/createSubscriptionCheckoutSession",
        location.origin
      );
      routeURL.searchParams.append("success_url", `${window.location.origin}/myCards`);
      routeURL.searchParams.append("cancel_url", `${window.location.href}`);
      routeURL.searchParams.append("subscription_tier", tier);

      const res = await fetch(routeURL, {
        method: "POST",
        cache: "no-cache",
      });
      const data = await res.json();
      if (!res.ok) {
        return { error: data.error || "UNKNOWN_ERROR", message: data.message, status: res.status };
      }
      return data as { session_url: string };
    },
    onMutate: (tier) => {
      setLoading(tier);
    },
    onSuccess: (data) => {
      if ("session_url" in data) {
        router.push(data.session_url);
      } else if (data.error === "ALREADY_SUBSCRIBED") {
        setIsAlreadySubscribed(true);
      } else {
        console.error(data.error);
        alert("Unable to start your subscription. Please try again.");
      }
    },
    onError: () => {
      alert("Something went wrong. Please try again or contact support@wrap.gifts");
    },
    onSettled: () => {
      setLoading(null);
    },
  });

  const handleCheckout = (tier: SubscriptionTier) => {
    return () => {
      if (!user) {
        showAuthenticator();
        return;
      }
      if (!isLoading) mutate(tier);
    };
  };

  const features = [
    "Easily customize eCards",
    "Send original art",
    "Celebrate sustainably",
    "Support our women-owned small business",
  ];

  return (
    <>
      <SupportFormModal
        visible={showSupportModal}
        hide={() => setShowSupportModal(false)}
      />
      <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      {/* Hero Section */}
      <section className="px-4 pb-16 pt-12 text-center sm:pt-20">
        <h1 className="mb-4 text-4xl font-bold text-gray-900 sm:text-5xl">
          Send Joy, Cancel Anytime
        </h1>
        <p className="mx-auto max-w-2xl text-xl text-gray-600">
          Send unlimited animated greeting cards to the people you love.
          <br className="hidden sm:block" />
          Beautiful, personal, and sustainable.
        </p>
      </section>

      {/* Already Subscribed Message */}
      {isAlreadySubscribed && (
        <section className="mx-auto max-w-2xl px-4 pb-8">
          <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-6 text-center shadow-lg">
            <span className="mb-2 block text-4xl">🎉</span>
            <h2 className="mb-2 text-2xl font-bold text-green-800">
              You're already a member!
            </h2>
            <p className="mb-4 text-green-700">
              Thank you for supporting sustainable gifting. You have full access to all our cards.
            </p>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                href="/myAccount"
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
              >
                Manage your subscription
              </Link>
              <Link
                href="/"
                className="inline-flex items-center gap-2 rounded-lg border-2 border-green-600 px-6 py-3 font-semibold text-green-700 transition-colors hover:bg-green-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
              >
                Browse Cards
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Pricing Cards */}
      <section className="mx-auto max-w-5xl px-4 pb-20">
        <div className="grid gap-8 md:grid-cols-3">
          {/* Free Tier */}
          <div className="flex select-none justify-center">
            <div className="flex w-full max-w-[300px] flex-col rounded-2xl border-2 border-green-300 bg-green-50 px-6 py-8 shadow-xl transition-transform hover:scale-105">
              <div className="relative">
                <h2 className="text-xl font-semibold text-green-800">Free</h2>
                <span className="absolute -right-2 -top-3 rounded-full bg-green-500 px-3 py-1 text-xs font-bold text-white">
                  Start Here
                </span>
              </div>
              <div className="py-4">
                <span className="text-5xl font-extrabold text-green-800">$0</span>
                <span className="text-lg text-green-700">/forever</span>
              </div>
              <div className="flex h-full flex-col justify-between">
                <ul className="flex flex-col gap-3 pb-6 text-left text-green-900">
                  <li className="flex items-start gap-2">
                    <span className="mt-1 text-green-500">&#10003;</span>
                    <span>Access to free card templates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 text-green-500">&#10003;</span>
                    <span>Customize with your own text</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 text-green-500">&#10003;</span>
                    <span>Send to anyone via link</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 text-green-500">&#10003;</span>
                    <span>No credit card required</span>
                  </li>
                </ul>
                <Link
                  href="/free"
                  className="grid w-full justify-center items-center rounded-lg bg-green-600 py-3 text-base font-bold text-white transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-green-500"
                >
                  Browse Free Cards
                </Link>
              </div>
            </div>
          </div>

          <PricingCard
            spinnerClassName="w-5 h-5"
            handleClickButton={handleCheckout("Monthly")}
            buttonClassName="bg-violet-600 hover:bg-violet-700 text-white"
            title="Monthly"
            price="$4"
            period="month"
            description={["✨ Unlimited premium cards", ...features, "Just the cost of one paper card!"]}
            loadingState={loading}
          />
          <PricingCard
            highlighted
            discount={
              <span className="absolute -right-2 -top-3 rounded-full bg-amber-400 px-3 py-1 text-xs font-bold text-amber-900">
                Save 50%
              </span>
            }
            spinnerClassName="w-5 h-5 text-violet-700"
            handleClickButton={handleCheckout("Annual")}
            loadingState={loading}
            title="Annual"
            price="$24"
            period="year"
            buttonClassName="bg-white hover:bg-violet-50 text-violet-700"
            description={["✨ Unlimited premium cards", ...features, "Best value — billed once a year"]}
          />
        </div>

        {/* Cancel Anytime Note */}
        <p className="mt-8 text-center text-sm text-gray-500">
          Paid memberships renew automatically. Cancel anytime from your profile
          page &mdash; no questions asked.
        </p>
      </section>

      {/* Value Props (EEATS) */}
      <section className="bg-white px-4 py-16">
        <div className="mx-auto max-w-5xl">
          <h2 className="mb-12 text-center text-3xl font-bold text-gray-900">
            Why Choose Wrap.Gifts?
          </h2>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
            <ValueProp
              icon="&#9889;"
              title="Easy"
              description="Customize in minutes with our intuitive editor"
            />
            <ValueProp
              icon="&#10084;"
              title="Emotional"
              description="Make memories that last with animated greetings"
            />
            <ValueProp
              icon="&#9749;"
              title="Accessible"
              description="Less than a coffee per month for unlimited cards"
            />
            <ValueProp
              icon="&#10004;"
              title="Trustworthy"
              description="Cancel anytime, no questions asked"
            />
            <ValueProp
              icon="&#127793;"
              title="Sustainable"
              description="Zero paper waste, 100% heart"
            />
          </div>
          <div className="mt-8 text-center">
            <a
              href="https://about.wrapdotgifts.com"
              className="inline-flex items-center gap-1 text-violet-600 hover:text-violet-800 font-medium transition-colors"
            >
              Learn more about our mission
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* Trust Footer */}
      <section className="bg-violet-50 px-4 py-12">
        <div className="mx-auto max-w-4xl">
          {/* Trust Badges */}
          <div className="mb-8 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            <div className="flex flex-col items-center text-center">
              <span className="mb-2 text-3xl" aria-hidden="true">👩‍💼</span>
              <span className="text-sm font-semibold text-violet-800">Women-Owned</span>
              <span className="text-xs text-gray-600">Small Business</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <span className="mb-2 text-3xl" aria-hidden="true">🌍</span>
              <span className="text-sm font-semibold text-violet-800">1% for the Planet</span>
              <span className="text-xs text-gray-600">Member via Stripe</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <span className="mb-2 text-3xl" aria-hidden="true">🌿</span>
              <span className="text-sm font-semibold text-violet-800">Climate Pledge</span>
              <span className="text-xs text-gray-600">Friendly Certified</span>
            </div>
          </div>

          {/* Contact */}
          <p className="text-center text-gray-600">
            Questions?{" "}
            {user ? (
              <button
                onClick={() => setShowSupportModal(true)}
                className="font-medium text-violet-600 hover:text-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
              >
                Contact Support
              </button>
            ) : (
              <>
                Reach out to us at{" "}
                <a
                  href="mailto:info@wrap.gifts"
                  className="font-medium text-violet-600 hover:text-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                >
                  info@wrap.gifts
                </a>
              </>
            )}
          </p>
        </div>
      </section>
      </div>
    </>
  );
};

export default PricingPage;
